import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import "@/index.css";
import { auth, mAuth } from "@/lib/api";

// Admin pages
import LoginPage from "@/pages/Login";
import Overview from "@/pages/Overview";
import Merchants from "@/pages/Merchants";
import MerchantDetail from "@/pages/MerchantDetail";
import Cards from "@/pages/Cards";
import UsersPage from "@/pages/Users";
import CardholderKyc from "@/pages/CardholderKyc";
import CardTypes from "@/pages/CardTypes";
import Fees from "@/pages/Fees";
import Transactions from "@/pages/Transactions";
import AuditLogs from "@/pages/AuditLogs";
import Analytics from "@/pages/Analytics";
import Webhooks from "@/pages/Webhooks";
import ApiDocs from "@/pages/ApiDocs";
import Settings from "@/pages/Settings";
import KybQueue from "@/pages/KybQueue";
import ProviderConfigs from "@/pages/ProviderConfigs";
import BalanceFunding from "@/pages/BalanceFunding";

// Merchant pages
import MerchantLogin from "@/pages/m/MerchantLogin";
import MerchantSignup from "@/pages/m/MerchantSignup";
import MerchantDashboard from "@/pages/m/MerchantDashboard";
import MerchantKyb from "@/pages/m/MerchantKyb";
import MerchantApiKeys from "@/pages/m/MerchantApiKeys";
import MerchantSecurity from "@/pages/m/MerchantSecurity";
import MerchantWebhooks from "@/pages/m/MerchantWebhooks";
import MerchantWebhookEvents from "@/pages/m/MerchantWebhookEvents";
import MerchantCardDetail from "@/pages/m/MerchantCardDetail";
import MerchantWallet from "@/pages/m/MerchantWallet";
import {
  MerchantCards, MerchantUsers, MerchantTransactions, MerchantAudit, MerchantUsage,
} from "@/pages/m/MerchantViews";


function Private({ children }) {
  if (!auth.isAuthed()) return <Navigate to="/login" replace />;
  return children;
}

function MerchantPrivate({ children }) {
  if (!mAuth.isAuthed()) return <Navigate to="/m/login" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        {/* Admin */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<Private><Overview /></Private>} />
        <Route path="/merchants" element={<Private><Merchants /></Private>} />
        <Route path="/merchants/:id" element={<Private><MerchantDetail /></Private>} />
        <Route path="/balance-funding" element={<Private><BalanceFunding /></Private>} />
        <Route path="/kyb-queue" element={<Private><KybQueue /></Private>} />
        <Route path="/providers" element={<Private><ProviderConfigs /></Private>} />
        <Route path="/cards" element={<Private><Cards /></Private>} />
        <Route path="/users" element={<Private><UsersPage /></Private>} />
        <Route path="/users/:userId/kyc" element={<Private><CardholderKyc portal="admin" /></Private>} />
        <Route path="/card-types" element={<Private><CardTypes /></Private>} />
        <Route path="/fees" element={<Private><Fees /></Private>} />
        <Route path="/transactions" element={<Private><Transactions /></Private>} />
        <Route path="/audit-logs" element={<Private><AuditLogs /></Private>} />
        <Route path="/analytics" element={<Private><Analytics /></Private>} />
        <Route path="/webhooks" element={<Private><Webhooks /></Private>} />
        <Route path="/api-docs" element={<Private><ApiDocs /></Private>} />
        <Route path="/settings" element={<Private><Settings /></Private>} />

        {/* Merchant */}
        <Route path="/m/login" element={<MerchantLogin />} />
        <Route path="/m/signup" element={<MerchantSignup />} />
        <Route path="/m" element={<MerchantPrivate><MerchantDashboard /></MerchantPrivate>} />
        <Route path="/m/wallet" element={<MerchantPrivate><MerchantWallet /></MerchantPrivate>} />
        <Route path="/m/kyb" element={<MerchantPrivate><MerchantKyb /></MerchantPrivate>} />
        <Route path="/m/api-keys" element={<MerchantPrivate><MerchantApiKeys /></MerchantPrivate>} />
        <Route path="/m/security" element={<MerchantPrivate><MerchantSecurity /></MerchantPrivate>} />
        <Route path="/m/webhooks" element={<MerchantPrivate><MerchantWebhooks /></MerchantPrivate>} />
        <Route path="/m/webhook-events" element={<MerchantPrivate><MerchantWebhookEvents /></MerchantPrivate>} />
        <Route path="/m/cards" element={<MerchantPrivate><MerchantCards /></MerchantPrivate>} />
        <Route path="/m/cards/:cardId" element={<MerchantPrivate><MerchantCardDetail /></MerchantPrivate>} />
        <Route path="/m/users" element={<MerchantPrivate><MerchantUsers /></MerchantPrivate>} />
        <Route path="/m/users/:userId/kyc" element={<MerchantPrivate><CardholderKyc portal="merchant" /></MerchantPrivate>} />
        <Route path="/m/transactions" element={<MerchantPrivate><MerchantTransactions /></MerchantPrivate>} />
        <Route path="/m/audit" element={<MerchantPrivate><MerchantAudit /></MerchantPrivate>} />
        <Route path="/m/usage" element={<MerchantPrivate><MerchantUsage /></MerchantPrivate>} />
        <Route path="/m/api-docs" element={<MerchantPrivate><ApiDocs /></MerchantPrivate>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
